import type { ReactNode } from "react";
import { ArrowDownIcon, ArrowUpIcon } from "@phosphor-icons/react";
import { Cell, Column, Row, Table, TableBody, TableHeader, type Key, type Selection, type SortDescriptor } from "react-aria-components";
import { Checkbox } from "./fields.tsx";
import { cx } from "../cx.ts";

export interface ColumnDef<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  isRowHeader?: boolean;
  allowsSorting?: boolean;
  align?: "start" | "end";
  className?: string;
  width?: string;
}

export interface DataTableProps<T extends { id: string }> {
  label: string;
  columns: ColumnDef<T>[];
  rows: T[];
  selectionMode?: "none" | "multiple";
  selectedKeys?: Selection;
  onSelectionChange?: (keys: Selection) => void;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (d: SortDescriptor) => void;
  onRowAction?: (key: Key) => void;
  rowHref?: (row: T) => string;
  empty?: ReactNode;
  selectAllLabel?: string;
}

/** Tabel op basis van React Aria: toetsenbordnavigatie, selectie, sorteren en rijacties. */
export function DataTable<T extends { id: string }>({ label, columns, rows, selectionMode = "none", selectedKeys, onSelectionChange, sortDescriptor, onSortChange, onRowAction, rowHref, empty, selectAllLabel }: DataTableProps<T>) {
  return (
    <div className="ui-table-wrap">
      <Table
        aria-label={label}
        className="ui-table"
        selectionMode={selectionMode}
        selectionBehavior="toggle"
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        onRowAction={onRowAction}
      >
        <TableHeader>
          {selectionMode === "multiple" ? (
            <Column className="ui-col ui-col--check">
              <Checkbox slot="selection" aria-label={selectAllLabel} />
            </Column>
          ) : null}
          {columns.map((c) => (
            <Column key={c.id} id={c.id} isRowHeader={c.isRowHeader} allowsSorting={c.allowsSorting} className={cx("ui-col", c.className)} style={{ width: c.width, textAlign: c.align === "end" ? "end" : undefined }}>
              {({ allowsSorting, sortDirection }) => (
                <span className="ui-col__sort">
                  {c.header}
                  {allowsSorting && sortDirection ? sortDirection === "ascending" ? <ArrowUpIcon aria-hidden /> : <ArrowDownIcon aria-hidden /> : null}
                </span>
              )}
            </Column>
          ))}
        </TableHeader>
        <TableBody items={rows} dependencies={[columns, selectionMode]} renderEmptyState={empty ? () => empty : undefined}>
          {(row) => (
            <Row id={row.id} className="ui-row" href={rowHref?.(row)}>
              {selectionMode === "multiple" ? (
                <Cell className="ui-cell ui-cell--check">
                  <Checkbox slot="selection" />
                </Cell>
              ) : null}
              {columns.map((col) => (
                <Cell key={col.id} className={cx("ui-cell", col.align === "end" && "ui-cell--num", col.className)}>
                  {col.cell(row)}
                </Cell>
              ))}
            </Row>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
